import type { ServiceFeatureCatalog } from '@/types/service-feature-catalog';
import type { EntityType } from '@/features/admin-settings/types/catalog-visibility';

/**
 * Drops one offering out of a catalog snapshot, along with any category it leaves empty.
 *
 * `GET /services/catalog` is served from a ten-minute server-side cache. Rather than spend a
 * refetch re-learning what the client already knows — and risk an API build predating the
 * eviction fix handing the offering straight back — the delete mutation edits every cached
 * snapshot directly.
 *
 * The catalog endpoint only ever returns a category that still has a member, so a group left
 * with nothing in it has to go too: otherwise it lingers as an empty "0/0 visible" card until
 * the next full reload, and then vanishes on its own.
 */
const withoutCatalogItem = (
  catalog: ServiceFeatureCatalog,
  entity: EntityType,
  id: string,
): ServiceFeatureCatalog => {
  if (entity === 'service') {
    const services = catalog.services.filter((service) => service.id !== id);
    const populatedCategoryIds = new Set(services.map((service) => service.categoryId));

    return {
      ...catalog,
      services,
      serviceCategories: catalog.serviceCategories.filter((category) =>
        populatedCategoryIds.has(category.id),
      ),
    };
  }

  return {
    ...catalog,
    features: catalog.features.filter((feature) => feature.id !== id),
    // A category keeps its own list of member ids, so the id has to come out of both places
    // or the group still counts a feature that is gone.
    featureCategories: catalog.featureCategories
      .map((category) => ({
        ...category,
        featureIds: category.featureIds.filter((featureId) => featureId !== id),
      }))
      .filter((category) => category.featureIds.length > 0),
  };
};

export default withoutCatalogItem;
